import type React from 'react'
import { Link } from 'react-router-dom'
import type { ConversationPlan, PlanStatus } from '@knownow/domain'
import { Avatar } from './avatar'

export interface PlanCardProps {
  readonly plan: ConversationPlan
  readonly orgSlug: string
  readonly ownerName: string | null
  readonly responses: { readonly completed: number; readonly target: number } | null
  readonly onDelete: (plan: ConversationPlan) => void
}

const statusLabel: Record<PlanStatus, string> = {
  draft: 'Draft',
  reviewing: 'Reviewing',
  active: 'Active',
  completed: 'Completed',
  archived: 'Archived',
}

const statusDotColor: Record<PlanStatus, string> = {
  draft: 'bg-muted-foreground',
  reviewing: 'bg-warning',
  active: 'bg-success',
  completed: 'bg-info',
  archived: 'bg-muted-foreground',
}

const statusTextColor: Record<PlanStatus, string> = {
  draft: 'text-muted-foreground',
  reviewing: 'text-warning-dark',
  active: 'text-success-dark',
  completed: 'text-info-dark',
  archived: 'text-muted-foreground',
}

function ClockIcon(): React.ReactElement {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="12" cy="12" r="10" />
      <polyline points="12 6 12 12 16 14" />
    </svg>
  )
}

function FolderIcon(): React.ReactElement {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
    </svg>
  )
}

function TrashIcon(): React.ReactElement {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <polyline points="3 6 5 6 21 6" />
      <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
      <path d="M10 11v6" />
      <path d="M14 11v6" />
      <path d="M9 6V4a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2" />
    </svg>
  )
}

function formatRelative(date: Date, now: Date = new Date()): string {
  const diffMs = now.getTime() - date.getTime()
  const minutes = Math.round(diffMs / 60000)
  if (minutes < 1) return 'just now'
  if (minutes < 60) return `${minutes}m ago`
  const hours = Math.round(minutes / 60)
  if (hours < 24) return `${hours}h ago`
  const days = Math.round(hours / 24)
  if (days === 1) return 'yesterday'
  if (days < 7) return `${days}d ago`
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
}

export function PlanCard({
  plan,
  orgSlug,
  ownerName,
  responses,
  onDelete,
}: PlanCardProps): React.ReactElement {
  const questionCount = plan.informationNeeds.length
  const pct =
    responses && responses.target > 0
      ? Math.round((responses.completed / responses.target) * 100)
      : null

  const handleDelete = (e: React.MouseEvent<HTMLButtonElement>): void => {
    // The card frame is also a link; prevent navigation when clicking the icon.
    e.preventDefault()
    e.stopPropagation()
    onDelete(plan)
  }

  return (
    <div
      className="group relative flex flex-col rounded-base border border-border bg-surface-elevated p-5 transition-all hover:border-primary/40 hover:shadow-md"
      data-testid="plan-card"
    >
      {/*
        The full-card link is the *bottom* layer. Sibling content sits above it
        with `pointer-events-none` so clicks fall through to the link, while
        interactive overlays (delete button) opt back in via `pointer-events-auto`.
      */}
      <Link
        to={`/${orgSlug}/plans/${plan.id}`}
        aria-label={`Open ${plan.title}`}
        className="absolute inset-0 z-0 rounded-base focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
      />

      <div className="pointer-events-none relative z-10 flex items-start justify-between gap-2">
        <span
          className={`inline-flex items-center gap-1.5 text-xs font-medium ${statusTextColor[plan.status]}`}
          data-testid="plan-status"
        >
          <span className={`h-1.5 w-1.5 rounded-full ${statusDotColor[plan.status]}`} />
          {statusLabel[plan.status]}
        </span>
        <button
          type="button"
          onClick={handleDelete}
          aria-label={`Delete ${plan.title}`}
          data-testid="plan-delete"
          className="pointer-events-auto -m-1 rounded-full p-1 text-muted-foreground opacity-0 transition-opacity hover:bg-destructive/10 hover:text-destructive focus-visible:opacity-100 group-hover:opacity-100"
        >
          <TrashIcon />
        </button>
      </div>

      <h3 className="pointer-events-none relative z-10 mt-3 font-serif text-xl leading-tight text-foreground">
        {plan.title}
      </h3>

      <div className="pointer-events-none relative z-10 mt-2 flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
        {plan.description && <span className="line-clamp-1">{plan.description}</span>}
        <span className="inline-flex items-center gap-1">
          <FolderIcon />
          {questionCount} {questionCount === 1 ? 'question' : 'questions'}
        </span>
      </div>

      {pct !== null && responses && (
        <div className="pointer-events-none relative z-10 mt-4">
          <div className="mb-1 flex items-baseline justify-between text-xs text-muted-foreground">
            <span>
              {responses.completed} of {responses.target} responded
            </span>
            <span className="font-medium text-foreground">{pct}%</span>
          </div>
          <div className="h-1.5 overflow-hidden rounded-full bg-muted">
            <div
              className="h-full rounded-full bg-primary transition-[width]"
              style={{ width: `${Math.min(100, pct)}%` }}
            />
          </div>
        </div>
      )}

      <div className="pointer-events-none relative z-10 mt-auto flex items-center justify-between gap-3 pt-4 text-xs text-muted-foreground">
        {ownerName ? (
          <span className="flex items-center gap-2">
            <Avatar name={ownerName} size="sm" />
            <span className="truncate text-foreground">{ownerName}</span>
          </span>
        ) : (
          <span />
        )}
        <span className="inline-flex items-center gap-1">
          <ClockIcon />
          {formatRelative(plan.updatedAt)}
        </span>
      </div>
    </div>
  )
}
